import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "className", "disabled", "error", "expanded", "id", "limitTagsShown", "onClear", "onKeyDown", "onTagClose", "editable", "renderInput", "renderTag", "size", "tags", "value"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useContext, useRef } from 'react';
import { INPUT_SIZES, KEY_CODES } from '../../constants';
import { ComboboxAdditional } from '../ComboboxAdditional/ComboboxAdditional';
import { FormFieldContext } from '../FormField/FormField.context';
import { Input } from '../Input/Input';
import { Tag } from '../Tag/Tag';
import { TagCloseButton } from '../TagCloseButton/TagCloseButton';
import { StyledComboboxInput } from './ComboboxInput.styles';

/**
 * @related ['Input', 'Tag']
 * @since 8.28.0
 */
export var ComboboxInput = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    className = _ref.className,
    disabledProp = _ref.disabled,
    errorProp = _ref.error,
    expanded = _ref.expanded,
    idProp = _ref.id,
    limitTagsShown = _ref.limitTagsShown,
    onClear = _ref.onClear,
    onKeyDown = _ref.onKeyDown,
    onTagClose = _ref.onTagClose,
    editable = _ref.editable,
    renderInput = _ref.renderInput,
    renderTag = _ref.renderTag,
    size = _ref.size,
    tagValues = _ref.tags,
    value = _ref.value,
    rest = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef();
  var context = useContext(FormFieldContext);
  var disabled = typeof disabledProp !== 'undefined' ? disabledProp : context.disabled;
  var error = typeof errorProp !== 'undefined' ? errorProp : context.error;
  var id = typeof idProp !== 'undefined' ? idProp : context.controlId;

  // Handle click on the tag close button
  var tagCloseCallback = useCallback(function (tag) {
    if (!disabled && typeof onTagClose === 'function') {
      onTagClose(tag.children);
    }
  }, [disabled, onTagClose]);

  // Focus on the actual input when clicking on the wrapper
  var wrapperClickCallback = useCallback(function () {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  // Preventing mouse down on the wrapper stops a blur which should not happen since clicking
  // on the wrapper is considered part of the input.
  // [CDS-2229]: We need to use stopPropagation when the dropdown is closed, otherwise
  // there was a weird bug where preventDefault was preventing the mouse event
  // to bubble up to the expand callback
  var wrapperMouseDownCallback = useCallback(function (e) {
    if (expanded) {
      e.preventDefault();
    } else {
      e.stopPropagation();
    }
  }, [expanded]);

  // Since we prevent default when mouse down on the wrapper, we must stop propagation when
  // mouse down on the input so that the event does not get prevented. If it were to get
  // prevented, we wouldn't be able to select text.
  var inputMouseDownCallback = useCallback(function (e) {
    return e.stopPropagation();
  }, []);

  // Handle keyboard interactions with the text input field
  var inputKeyDownCallback = useCallback(function (e) {
    // Close the last option on backspace
    if (!disabled && editable && tagValues && tagValues.length && e.keyCode === KEY_CODES.BACKSPACE && !value.length && typeof onTagClose === 'function') {
      onTagClose(tagValues[tagValues.length - 1]);
    }

    // Make sure we do not clobber keydown set directly on component
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [disabled, onKeyDown, onTagClose, editable, tagValues, value]);
  var clearButtonClickCallback = useCallback(function (e) {
    // Clicking on the clear button should not focus the input. This prevents the
    // event from bubbling to the wrapper and triggering that focus.
    e.stopPropagation();
    if (!disabled && typeof onClear === 'function') {
      onClear();
    }
  }, [disabled, onClear]);

  // This prevents the clear button from receiving focus.
  var clearButtonMouseDownCallback = useCallback(function (e) {
    return e.preventDefault();
  }, []);

  // This prevents the click event from bubbling to the wrapper and triggering focus.
  var tagClickCallback = useCallback(function (e) {
    return e.stopPropagation();
  }, []);

  // This prevents the tags from receiving focus.
  var tagMouseDownCallback = useCallback(function (e) {
    return e.preventDefault();
  }, []);

  // Display read only tags
  var inputValue = value;
  if (!editable && tagValues && tagValues.length) {
    inputValue = _toConsumableArray(tagValues);
    if (value) {
      inputValue.push(value);
    }
    inputValue = inputValue.join(', ');
  }
  var renderCloseButton = useCallback(function (props) {
    return /*#__PURE__*/React.createElement(TagCloseButton, _extends({}, props, {
      tabIndex: "-1"
    }));
  }, []);
  var tags;
  var additionalTags;
  var clear;
  if (editable && tagValues && tagValues.length) {
    tags = tagValues.map(function (tag) {
      var tagProps = {
        key: tag,
        isOpen: true,
        onClose: tagCloseCallback,
        renderCloseButton: renderCloseButton,
        onMouseDown: tagMouseDownCallback,
        onClick: tagClickCallback,
        children: tag
      };
      return typeof renderTag === 'function' ? renderTag(tagProps) : /*#__PURE__*/React.createElement(Tag, tagProps);
    });
    if (typeof limitTagsShown === 'number' && tags.length > limitTagsShown) {
      additionalTags = /*#__PURE__*/React.createElement(ComboboxAdditional, null, "+" + (tags.length - limitTagsShown));
      tags = tags.slice(0, limitTagsShown);
    }
    clear = /*#__PURE__*/React.createElement(TagCloseButton, {
      tabIndex: "-1",
      "aria-label": "Clear field",
      onClick: clearButtonClickCallback,
      onMouseDown: clearButtonMouseDownCallback
    });
  }
  var inputProps = Object.assign({}, rest, {
    'aria-expanded': typeof expanded === 'boolean' ? expanded : undefined,
    disabled: disabled,
    error: error,
    id: id,
    'ref': inputRef,
    'onKeyDown': inputKeyDownCallback,
    'onMouseDown': inputMouseDownCallback,
    'readOnly': editable ? undefined : true,
    'value': inputValue
  });
  return /*#__PURE__*/React.createElement(StyledComboboxInput, {
    appearance: appearance,
    className: className,
    isClearable: !!clear,
    disabled: disabled,
    error: error,
    expanded: expanded,
    onClick: wrapperClickCallback,
    onMouseDown: wrapperMouseDownCallback,
    ref: forwardedRef,
    size: size
  }, tags, additionalTags, renderInput(inputProps), clear);
});
ComboboxInput.displayName = 'ComboboxInput';
ComboboxInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * By default, comboboxes have the appearance of a Select
   * component (with an arrow indicator). You can change the appearance to `"input"` to
   * match the Input component.
   */
  appearance: PropTypes.oneOf(['input', 'select']),
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * Displays the input in a disabled state. Can also be inherited from a FormField parent.
   */
  disabled: PropTypes.bool,
  /**
   * Displays the input in an error state. Can also be inherited from a FormField parent.
   */
  error: PropTypes.bool,
  /**
   * When the `appearance` is `"select"`, the chevron can be reversed to indicate an expanded
   * state.
   */
  expanded: PropTypes.bool,
  /**
   * The native `id` attribute.
   */
  id: PropTypes.string,
  /**
   * You can limit the number of tags that are visible.
   */
  limitTagsShown: PropTypes.number,
  /**
   * The input's `onChange` callback.
   */
  onChange: PropTypes.func,
  /**
   * Called when the clear button is clicked.
   */
  onClear: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * Called when a tag close button is clicked, or when a tag is removed with backspace.
   */
  onTagClose: PropTypes.func,
  /**
   * When `false`, tags will render as non-interactive list of values.
   */
  editable: PropTypes.bool,
  /**
   * A render function that returns the input component.
   */
  renderInput: PropTypes.func,
  /**
   * A render function that returns the tag component.
   */
  renderTag: PropTypes.func,
  /**
   * Determines the size of the input.
   */
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
  /**
   * An array of tag values to display.
   */
  tags: PropTypes.arrayOf(PropTypes.string),
  /**
   * The input value.
   */
  value: PropTypes.string.isRequired
} : {};
ComboboxInput.defaultProps = {
  appearance: 'select',
  editable: true,
  renderInput: function renderInput(props) {
    return /*#__PURE__*/React.createElement(Input, props);
  },
  renderTag: function renderTag(props) {
    return /*#__PURE__*/React.createElement(Tag, props);
  }
};